import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Users, User, Shield, Code, Search, Edit2, X, CheckCircle,
    AlertCircle, Loader, ArrowLeft, UserPlus, Trash2, RefreshCw,
    ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Settings, MessageSquare, Bot,
    DollarSign, Activity, Clock, MapPin
} from 'lucide-react';
import { useAuth } from '../../context/UnifiedAuthContext.jsx';
import { adminAPI } from '../../utils/api.js';
import apiClient from '../../utils/api.js';
import { anonymizeName, anonymizePhone } from '../../utils/demoMode.js';
import GoogleReviews from '../GoogleReviews/GoogleReviews.jsx';
import PerformanceMetrics from '../PerformanceMetrics/PerformanceMetrics.jsx';
import IntegrationSettings from '../IntegrationSettings/IntegrationSettings.jsx';
import AppointmentTracker from '../AppointmentTracker/AppointmentTracker.jsx';
import PaymentManagement from './PaymentManagement.jsx';
import InvitationsTab from './InvitationsTab.jsx';
import ReviewLinksTab from './ReviewLinksTab.jsx';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal.jsx';
import './AdminPortal.scss';

const AdminPortal = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { urlParams, userRole, isAdmin, isManager } = useAuth();

    // State management
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [expandedUser, setExpandedUser] = useState(null);
    const [filterRole, setFilterRole] = useState('all');
    const [activeTab, setActiveTab] = useState('users');
    const [hasModifiedUsers, setHasModifiedUsers] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [pagination, setPagination] = useState(null);
    const [userToDelete, setUserToDelete] = useState(null);
    const usersHeading = useRef(null);
    const isDeactivation = typeof userToDelete?.id === 'string'
        && userToDelete.id.startsWith('ghl_mapping_');
    const deletionName = anonymizeName(userToDelete?.name || 'this user');
    
    // Auto-match service state
    const [autoMatchStatus, setAutoMatchStatus] = useState(null);
    const [autoMatchLoading, setAutoMatchLoading] = useState(false);

    // Check admin access
    useEffect(() => {
        if (!isAdmin() && !isManager()) {
            navigate(
                { pathname: '/', search: location.search },
                { state: { error: 'Access denied. Admin privileges required.' } }
            );
        }
    }, [isAdmin, isManager, navigate, location.search]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('googleBusiness') === 'connected') {
            setActiveTab('google-reviews');
        }
    }, [location.search]);

    // Initial fetch on mount
    useEffect(() => {
        fetchUsers(1);
    }, []);

    // Fetch auto-match status when tab becomes active
    useEffect(() => {
        if (activeTab === 'auto-match') {
            fetchAutoMatchStatus();
        }
    }, [activeTab]);

    // Refetch when itemsPerPage changes
    useEffect(() => {
        if (currentPage > 0) { // Avoid initial render
            setCurrentPage(1);
            fetchUsers(1);
        }
    }, [itemsPerPage]);

    // Refetch when filter role changes
    useEffect(() => {
        setCurrentPage(1); // Reset to first page when filtering
        fetchUsers(1);
    }, [filterRole]);

    // Auto-match service functions
    const fetchAutoMatchStatus = async () => {
        try {
            const response = await apiClient.get(apiClient.addUrlParams('/api/admin/review-auto-match/status'));
            setAutoMatchStatus(response.data);
        } catch (error) {
            console.error('Error fetching auto-match status:', error);
        }
    };

    const handleAutoMatchStart = async () => {
        setAutoMatchLoading(true);
        try {
            await apiClient.post(apiClient.addUrlParams('/api/admin/review-auto-match/start'));
            setSuccess('Auto-match service started successfully');
            setTimeout(() => setSuccess(null), 5000);
            await fetchAutoMatchStatus();
        } catch (error) {
            setError('Failed to start auto-match service');
            setTimeout(() => setError(null), 8000);
        } finally {
            setAutoMatchLoading(false);
        }
    };

    const handleAutoMatchStop = async () => {
        setAutoMatchLoading(true);
        try {
            await apiClient.post(apiClient.addUrlParams('/api/admin/review-auto-match/stop'));
            setSuccess('Auto-match service stopped successfully');
            setTimeout(() => setSuccess(null), 5000);
            await fetchAutoMatchStatus();
        } catch (error) {
            setError('Failed to stop auto-match service');
            setTimeout(() => setError(null), 8000);
        } finally {
            setAutoMatchLoading(false);
        }
    };

    const handleManualSync = async () => {
        setAutoMatchLoading(true);
        try {
            await apiClient.post(apiClient.addUrlParams('/api/admin/review-auto-match/trigger'));
            setSuccess('Manual sync completed successfully');
            setTimeout(() => setSuccess(null), 5000);
            await fetchAutoMatchStatus();
        } catch (error) {
            setError('Manual sync failed');
            setTimeout(() => setError(null), 8000);
        } finally {
            setAutoMatchLoading(false);
        }
    };

    const fetchUsers = async (page = currentPage, search = searchTerm, role = filterRole) => {
        setLoading(true);
        setError(null);
        try {
            // Ensure page is a valid number
            const pageNumber = typeof page === 'number' ? page : parseInt(page) || 1;
            const data = await adminAPI.getUserMappings(null, pageNumber, itemsPerPage, search, role);
            setUsers(data.mappings || []);
            setFilteredUsers(data.mappings || []); // Set both since filtering is now server-side
            setPagination(data.pagination);
            setCurrentPage(pageNumber);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Search handler functions
    const handleSearch = (event) => {
        event.preventDefault();
        const newSearchTerm = searchInput.trim();
        setSearchTerm(newSearchTerm);
        setCurrentPage(1); // Reset to page 1 when searching
        fetchUsers(1, newSearchTerm, filterRole);
    };

    const handleClearSearch = () => {
        setSearchInput('');
        setSearchTerm('');
        setCurrentPage(1);
        fetchUsers(1, '', filterRole);
    };

    const handleSearchInputChange = (event) => {
        setSearchInput(event.target.value);
    };



    const handleSaveUser = async (userData) => {
        setLoading(true);
        setError(null);
        try {
            if (userData.id) {
                // Use the existing updateUserMapping endpoint - it already handles GoHighLevel virtual mappings
                await adminAPI.updateUserMapping(userData.id, userData);
            } else {
                await adminAPI.createUserMapping(userData);
            }

            setSuccess('User saved successfully');
            setEditingUser(null);
            setHasModifiedUsers(true);
            await fetchUsers(currentPage, searchTerm, filterRole);
            
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;
        const userId = userToDelete.id;
        const isGoHighLevelUser = typeof userId === 'string' && userId.startsWith('ghl_mapping_');

        setLoading(true);
        setError(null);
        try {
            // Use the existing deleteUserMapping endpoint - it already handles GoHighLevel virtual mappings
            await adminAPI.deleteUserMapping(userId);
            
            const successMessage = isGoHighLevelUser 
                ? 'Technician deactivated successfully'
                : 'User deleted successfully';
            setSuccess(successMessage);
            
            setHasModifiedUsers(true);
            await fetchUsers(currentPage, searchTerm, filterRole);
            
            setTimeout(() => setSuccess(null), 3000);
        } finally {
            setLoading(false);
        }
    };


    const handleNavigateBack = (shouldRefresh = false) => {
        const searchParams = new URLSearchParams();
        
        // Preserve all URL parameters
        if (urlParams.location) searchParams.set('location', urlParams.location);
        if (urlParams.user) searchParams.set('user', urlParams.user);
        // Preserve integration parameter for GoHighLevel users
        if (urlParams.integration) searchParams.set('integration', urlParams.integration);
        
        if (shouldRefresh) {
            searchParams.set('refresh', Date.now().toString());
        }
        navigate('/dashboard?' + searchParams.toString());
    };

    const UserEditForm = ({ user, onSave, onCancel }) => {
        const [formData, setFormData] = useState({
            ejentoUserId: user?.ejentoUserId || '',
            ejentoLocationId: user?.ejentoLocationId || urlParams?.location || '',
            technicianId: user?.technicianId || '',
            userRole: user?.userRole || 'technician',
            name: user?.name || '',
            email: user?.email || '',
            crmCode: user?.crmCode || '',
            isActive: user?.isActive !== undefined ? user.isActive : true,
            permissions: user?.permissions || {}
        });

        return (
            <div className="user-edit-form">
                <h3>{user ? 'Edit User' : 'Create User Mapping'}</h3>
                
                <div className="form-grid">
                    <div className="form-group">
                        <label>Ejento User ID* {user?.id ? '(Current User)' : '(Auto-generated)'}</label>
                        <input
                            type="text"
                            value={formData.ejentoUserId || (user?.id ? '' : 'Will be auto-generated')}
                            readOnly
                            placeholder={user?.id ? "Current user ID" : "Auto-generated random ID"}
                            className="readonly-field"
                        />
                    </div>

                    <div className="form-group">
                        <label>Ejento Location ID* (Auto-filled)</label>
                        <input
                            type="text"
                            value={formData.ejentoLocationId}
                            readOnly
                            placeholder="Location ID from current session"
                            className="readonly-field"
                        />
                    </div>


                    <div className="form-group">
                        <label>Name</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            placeholder="User's display name"
                        />
                    </div>

                    <div className="form-group">
                        <label>Email</label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            placeholder="User's email address"
                        />
                    </div>

                    <div className="form-group">
                        <label>CRM Code (Auto-generated)</label>
                        <input
                            type="text"
                            value={formData.crmCode || 'Will be auto-generated'}
                            readOnly
                            placeholder="Auto-generated based on technician ID"
                            className="readonly-field"
                        />
                    </div>

                    <div className="form-group">
                        <label>Role*</label>
                        <select
                            value={formData.userRole}
                            onChange={(e) => setFormData({...formData, userRole: e.target.value})}
                        >
                            <option value="technician">Technician</option>
                            <option value="manager">Manager</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={formData.isActive}
                                onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                            />
                            <span>Active</span>
                        </label>
                    </div>
                </div>

                <div className="form-actions">
                    <button 
                        className="btn btn-secondary" 
                        onClick={onCancel}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button 
                        className="btn btn-primary" 
                        onClick={() => onSave({...user, ...formData})}
                        disabled={loading || !formData.ejentoLocationId || (user?.id && !formData.ejentoUserId)}
                    >
                        {loading ? <Loader className="spin" /> : 'Save'}
                    </button>
                </div>
            </div>
        );
    };


    return (
        <div className="admin-portal">
            <ConfirmationModal
                isOpen={Boolean(userToDelete)}
                onCancel={() => setUserToDelete(null)}
                onConfirm={handleDeleteUser}
                title={isDeactivation ? 'Deactivate technician?' : 'Delete user mapping?'}
                message={isDeactivation
                    ? `Deactivate ${deletionName}? This will disable their access and preserve their data.`
                    : `Delete the user mapping for ${deletionName}?`}
                confirmText={isDeactivation ? 'Deactivate technician' : 'Delete user mapping'}
                pendingText={isDeactivation ? 'Deactivating...' : 'Deleting...'}
                variant="danger"
                returnFocusRef={usersHeading}
            />
            <div className="admin-header">
                <div className="header-content">
                    <button 
                        className="back-button"
                        onClick={() => handleNavigateBack(hasModifiedUsers)}
                    >
                        <ArrowLeft /> Back to Dashboard
                    </button>
                    <h1>
                        <Shield className="header-icon" />
                        Admin Portal
                    </h1>
                    <div className="header-info">
                        <span className="role-badge">
                            {userRole === 'admin' ? <Shield /> : <Users />}
                            {userRole}
                        </span>
                    </div>
                </div>
            </div>

            {/* Notifications */}
            <AnimatePresence>
                {error && (
                    <motion.div 
                        className="notification error"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                    >
                        <AlertCircle />
                        {error}
                        <button onClick={() => setError(null)}>
                            <X />
                        </button>
                    </motion.div>
                )}
                
                {success && (
                    <motion.div 
                        className="notification success"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                    >
                        <CheckCircle />
                        {success}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Section Header */}
            <div className="admin-section-header">
                <h2>
                    <Users /> User Management
                </h2>
            </div>

            {/* Navigation Tabs */}
            <div className="admin-tabs">
                <div className="tabs-container">
                    <button
                        className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => setActiveTab('users')}
                    >
                        <Users /> User Management
                    </button>
                    {isAdmin() && (
                        <button
                            className={`tab-button ${activeTab === 'invitations' ? 'active' : ''}`}
                            onClick={() => setActiveTab('invitations')}
                        >
                            <UserPlus /> Invitations
                        </button>
                    )}
                    <button
                        className={`tab-button ${activeTab === 'google-reviews' ? 'active' : ''}`}
                        onClick={() => setActiveTab('google-reviews')}
                    >
                        <Code /> Google Reviews
                    </button>
                    {isAdmin() && (
                        <button
                            className={`tab-button ${activeTab === 'review-links' ? 'active' : ''}`}
                            onClick={() => setActiveTab('review-links')}
                        >
                            <MapPin /> Review Links
                        </button>
                    )}
                    <button
                        className={`tab-button ${activeTab === 'performance' ? 'active' : ''}`}
                        onClick={() => setActiveTab('performance')}
                    >
                        <Shield /> Performance Metrics
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'integration' ? 'active' : ''}`}
                        onClick={() => setActiveTab('integration')}
                    >
                        <Settings /> Integration
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'appointments' ? 'active' : ''}`}
                        onClick={() => setActiveTab('appointments')}
                    >
                        <MessageSquare /> Appointment Tracker
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'auto-match' ? 'active' : ''}`}
                        onClick={() => setActiveTab('auto-match')}
                    >
                        <Bot /> Auto-Match
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'payments' ? 'active' : ''}`}
                        onClick={() => setActiveTab('payments')}
                    >
                        <DollarSign /> Payments
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="admin-content">
                {activeTab === 'invitations' && isAdmin() && (
                    <InvitationsTab />
                )}

                {activeTab === 'users' && (
                    <div className="users-tab">
                        <motion.div
                            className="config-header"
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <div className="header-content">
                                <div className="logo-section">
                                    <Users className="logo" />
                                    <div>
                                        <h1 ref={usersHeading} tabIndex={-1}>User Management</h1>
                                        <p>Manage system users, roles, and permissions</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            className="controls-section"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                        >
                            <div className="controls-bar">
                            <div className="search-box">
                                <form onSubmit={handleSearch} className="search-form">
                                    <div className="search-input-wrapper">
                                        <Search className="search-icon" />
                                        <input
                                            type="text"
                                            placeholder="Search by name, email, or user ID..."
                                            value={searchInput}
                                            onChange={handleSearchInputChange}
                                            className="search-input"
                                        />
                                        {(searchInput || searchTerm) && (
                                            <button
                                                type="button"
                                                onClick={handleClearSearch}
                                                className="clear-search-btn"
                                                title="Clear search"
                                            >
                                                <X size={16} />
                                            </button>
                                        )}
                                    </div>
                                    <button type="submit" className="search-btn">
                                        Search
                                    </button>
                                </form>
                            </div>
                            
                            <div className="filter-controls">
                                <select
                                    value={filterRole}
                                    onChange={(e) => setFilterRole(e.target.value)}
                                    className="role-filter"
                                >
                                    <option value="all">All Roles</option>
                                    <option value="technician">Technicians</option>
                                    <option value="manager">Managers</option>
                                    <option value="admin">Admins</option>
                                </select>
                                
                                <button 
                                    className="btn btn-primary"
                                    onClick={() => setEditingUser({})}
                                >
                                    <UserPlus /> Add User
                                </button>
                                
                                <button 
                                    className="btn btn-secondary"
                                    onClick={() => fetchUsers(currentPage, searchTerm, filterRole)}
                                    disabled={loading}
                                >
                                    <RefreshCw className={loading ? 'spin' : ''} />
                                </button>
                                
                                {/* Debug button for GoHighLevel - fix technician roles */}
                                {urlParams.integration && (
                                    <button 
                                        className="btn btn-primary"
                                        onClick={async () => {
                                            try {
                                                setLoading(true);
                                                const result = await adminAPI.updateTechnicianRoles();
                                                setSuccess(`Updated ${result.updated} technicians.`);
                                                setTimeout(() => setSuccess(null), 5000);
                                                await fetchUsers(currentPage, searchTerm, filterRole);
                                            } catch (err) {
                                                setError(err.message);
                                            } finally {
                                                setLoading(false);
                                            }
                                        }}
                                        disabled={loading}
                                    >
                                        Fix Roles
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Search Results Info */}
                        {searchTerm && (
                            <div className="search-results-info">
                                <span>Searching for: "{searchTerm}"</span>
                                <span className="results-count">
                                    {pagination?.total || 0} result{pagination?.total !== 1 ? 's' : ''} found
                                </span>
                            </div>
                        )}
                        </motion.div>

                        {editingUser && (
                            <div className="edit-overlay">
                                <motion.div 
                                    className="edit-modal"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                >
                                    <UserEditForm
                                        user={editingUser}
                                        onSave={handleSaveUser}
                                        onCancel={() => setEditingUser(null)}
                                    />
                                </motion.div>
                            </div>
                        )}


                        <motion.div
                            className="users-list"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                        >
                            {loading && !editingUser ? (
                                <div className="loading-state">
                                    <Loader className="spin" size={48} />
                                    <h3>Loading users...</h3>
                                    <p>Fetching user information</p>
                                </div>
                            ) : filteredUsers.length === 0 ? (
                                <div className="empty-state">
                                    <Users size={48} />
                                    <h3>{searchTerm ? 'No users match your search' : 'No users found'}</h3>
                                    <p>
                                        {searchTerm
                                            ? `Try adjusting your search term "${searchTerm}" or clear the search to see all users.`
                                            : 'Get started by adding your first user to the system.'
                                        }
                                    </p>
                                </div>
                            ) : (
                                filteredUsers.map(user => {
                                    const displayName = anonymizeName(user.name || 'Unnamed User');

                                    return (
                                        <motion.div
                                            key={user.id}
                                            className={`user-card ${!user.isActive ? 'inactive' : ''}`}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                        >
                                            <div className="user-header" onClick={() => setExpandedUser(expandedUser === user.id ? null : user.id)}>
                                                <div className="user-info">
                                                    <h3>{displayName}</h3>
                                                    <div className="user-meta">
                                                        <span className="user-id">ID: {user.ejentoUserId}</span>
                                                        <span className={`role-badge ${user.userRole}`}>
                                                            {user.userRole === 'admin' && <Shield />}
                                                            {user.userRole === 'manager' && <Users />}
                                                            {user.userRole}
                                                        </span>
                                                        {!user.isActive && <span className="inactive-badge">Inactive</span>}
                                                    </div>
                                                </div>
                                            <div className="user-actions">
                                                <button 
                                                    className="btn btn-icon"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditingUser(user);
                                                    }}
                                                >
                                                    <Edit2 />
                                                </button>
                                                {isAdmin() && (
                                                    <button 
                                                        className="btn btn-icon btn-danger"
                                                        aria-label={typeof user.id === 'string' && user.id.startsWith('ghl_mapping_')
                                                            ? `Deactivate technician ${displayName}`
                                                            : `Delete user mapping for ${displayName}`}
                                                        disabled={loading}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setUserToDelete(user);
                                                        }}
                                                    >
                                                        <Trash2 />
                                                    </button>
                                                )}
                                                {expandedUser === user.id ? <ChevronUp /> : <ChevronDown />}
                                            </div>
                                        </div>
                                        
                                        {expandedUser === user.id && (
                                            <motion.div
                                                className="user-details"
                                                initial={{ height: 0 }}
                                                animate={{ height: 'auto' }}
                                            >
                                                <div className="detail-row">
                                                    <span className="label">Email:</span>
                                                    <span>{user.email ? anonymizeName(user.email.split('@')[0]) + '@example.com' : 'Not provided'}</span>
                                                </div>
                                                <div className="detail-row">
                                                    <span className="label">Location ID:</span>
                                                    <span>{user.ejentoLocationId}</span>
                                                </div>
                                                {user.technicianId && (
                                                    <div className="detail-row">
                                                        <span className="label">Technician ID:</span>
                                                        <span>{user.technicianId}</span>
                                                    </div>
                                                )}
                                                <div className="detail-row">
                                                    <span className="label">Created:</span>
                                                    <span>{new Date(user.createdAt).toLocaleDateString()}</span>
                                                </div>
                                                <div className="detail-row">
                                                    <span className="label">Last Access:</span>
                                                    <span>{user.lastAccessAt ? new Date(user.lastAccessAt).toLocaleString() : 'Never'}</span>
                                                </div>
                                            </motion.div>
                                        )}
                                    </motion.div>
                                    );
                                })
                            )}
                        </motion.div>

                        {/* Pagination Controls */}
                        {pagination && pagination.totalPages > 1 && (
                            <div className="pagination-controls">
                                <div className="pagination-info">
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
                                </div>
                                
                                <div className="pagination-buttons">
                                    <button 
                                        className="btn btn-icon" 
                                        onClick={() => fetchUsers(currentPage - 1, searchTerm, filterRole)}
                                        disabled={currentPage === 1}
                                    >
                                        <ChevronLeft />
                                    </button>
                                    
                                    <div className="page-numbers">
                                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (pagination.totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= pagination.totalPages - 2) {
                                                pageNum = pagination.totalPages - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }
                                            
                                            return (
                                                <button
                                                    key={pageNum}
                                                    className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                                    onClick={() => fetchUsers(pageNum, searchTerm, filterRole)}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    
                                    <button 
                                        className="btn btn-icon" 
                                        onClick={() => fetchUsers(currentPage + 1, searchTerm, filterRole)}
                                        disabled={currentPage === pagination.totalPages}
                                    >
                                        <ChevronRight />
                                    </button>
                                </div>
                                
                                <div className="items-per-page">
                                    <label>Items per page:</label>
                                    <select 
                                        value={itemsPerPage} 
                                        onChange={(e) => {
                                            const newLimit = parseInt(e.target.value);
                                            setItemsPerPage(newLimit);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="10">10</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                
                {activeTab === 'google-reviews' && (
                    <GoogleReviews />
                )}

                {activeTab === 'review-links' && isAdmin() && (
                    <ReviewLinksTab />
                )}
                
                {activeTab === 'performance' && (
                    <PerformanceMetrics />
                )}
                
                {activeTab === 'integration' && (
                    <IntegrationSettings />
                )}
                
                {activeTab === 'appointments' && (
                    <AppointmentTracker />
                )}

                {activeTab === 'auto-match' && (
                    <div className="auto-match-tab">
                        <motion.div
                            className="config-header"
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <div className="header-content">
                                <div className="logo-section">
                                    <Bot className="logo" />
                                    <div>
                                        <h1>Review Auto-Match Service</h1>
                                        <p>Automatically sync and match Google reviews to technicians based on names mentioned in review text</p>
                                    </div>
                                </div>

                                {autoMatchStatus && (
                                    <div className={`status-badge ${autoMatchStatus.isRunning ? 'active' : 'inactive'}`}>
                                        {autoMatchStatus.isRunning ? (
                                            <>
                                                <CheckCircle size={16} />
                                                <span>Running</span>
                                            </>
                                        ) : (
                                            <>
                                                <AlertCircle size={16} />
                                                <span>Stopped</span>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </motion.div>

                        {/* Service Status */}
                        <motion.div
                            className="integration-status"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                        >
                            <div className="status-header">
                                <h3><Activity size={20} /> Service Status</h3>
                            </div>

                            {autoMatchStatus && (
                                <div className="status-grid">
                                    <div className="status-item">
                                        <Clock className="status-icon success" />
                                        <div>
                                            <strong>Sync Frequency</strong>
                                            <span>Every {autoMatchStatus.syncFrequencyHours} hours</span>
                                        </div>
                                    </div>
                                    {autoMatchStatus.nextSyncAt && (
                                        <div className="status-item">
                                            <RefreshCw className="status-icon" />
                                            <div>
                                                <strong>Next Sync</strong>
                                                <span>{new Date(autoMatchStatus.nextSyncAt).toLocaleString()}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </motion.div>

                        {/* Assignment Issues Statistics */}
                        {autoMatchStatus?.assignmentIssues && (
                            <motion.div
                                className="assignment-issues-card"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                            >
                                <div className="card-header">
                                    <h3><AlertCircle size={20} /> Reviews Requiring Manual Assignment</h3>
                                </div>
                                <div className="issues-grid">
                                    <div className="issue-stat">
                                        <div className="stat-icon multiple-techs">
                                            <Users size={24} />
                                        </div>
                                        <div className="stat-content">
                                            <div className="stat-value">{autoMatchStatus.assignmentIssues.multipleTechs}</div>
                                            <div className="stat-label">Multiple Technicians</div>
                                            <div className="stat-description">Reviews mentioning multiple tech names</div>
                                        </div>
                                    </div>
                                    <div className="issue-stat">
                                        <div className="stat-icon ambiguous-name">
                                            <AlertCircle size={24} />
                                        </div>
                                        <div className="stat-content">
                                            <div className="stat-value">{autoMatchStatus.assignmentIssues.ambiguousName}</div>
                                            <div className="stat-label">Ambiguous Names</div>
                                            <div className="stat-description">Same name, multiple technicians</div>
                                        </div>
                                    </div>
                                    <div className="issue-stat">
                                        <div className="stat-icon total-problematic">
                                            <MessageSquare size={24} />
                                        </div>
                                        <div className="stat-content">
                                            <div className="stat-value">{autoMatchStatus.assignmentIssues.totalProblematic}</div>
                                            <div className="stat-label">Total Requiring Attention</div>
                                            <div className="stat-description">Reviews needing manual review</div>
                                        </div>
                                    </div>
                                    <div className="issue-stat">
                                        <div className="stat-icon unassigned">
                                            <User size={24} />
                                        </div>
                                        <div className="stat-content">
                                            <div className="stat-value">{autoMatchStatus.assignmentIssues.totalUnassigned}</div>
                                            <div className="stat-label">Total Unassigned</div>
                                            <div className="stat-description">All unassigned reviews</div>
                                        </div>
                                    </div>
                                </div>
                                {(autoMatchStatus.assignmentIssues.multipleTechs > 0 || autoMatchStatus.assignmentIssues.ambiguousName > 0) && (
                                    <div className="action-prompt">
                                        <AlertCircle size={16} />
                                        <span>Visit the Google Reviews tab to manually assign these reviews</span>
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {/* Controls */}
                        <motion.div
                            className="controls-card"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            <h3><Settings size={20} /> Service Controls</h3>
                            <div className="button-group">
                                <button 
                                    className="button button-primary" 
                                    onClick={handleAutoMatchStart}
                                    disabled={
                                        autoMatchLoading ||
                                        !autoMatchStatus?.canControlBackground ||
                                        autoMatchStatus?.isRunning
                                    }
                                >
                                    {autoMatchLoading ? <Loader className="spinning" /> : <CheckCircle />}
                                    Start Service
                                </button>
                                
                                <button 
                                    className="button button-secondary" 
                                    onClick={handleAutoMatchStop}
                                    disabled={
                                        autoMatchLoading ||
                                        !autoMatchStatus?.canControlBackground ||
                                        !autoMatchStatus?.isRunning
                                    }
                                >
                                    {autoMatchLoading ? <Loader className="spinning" /> : <X />}
                                    Stop Service
                                </button>
                                
                                <button 
                                    className="button button-accent" 
                                    onClick={handleManualSync}
                                    disabled={autoMatchLoading}
                                >
                                    {autoMatchLoading ? <Loader className="spinning" /> : <RefreshCw />}
                                    Manual Sync Now
                                </button>
                            </div>
                        </motion.div>

                        {/* How it Works */}
                        <motion.div
                            className="info-card"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                        >
                            <h3><Bot size={20} /> How Auto-Match Works</h3>
                            <ul>
                                <li>
                                    <strong>Background matching:</strong>{' '}
                                    {autoMatchStatus?.backgroundEnabled
                                        ? `Runs every ${autoMatchStatus.syncFrequencyHours} hour(s).`
                                        : 'Disabled; use Manual Sync Now to process this location only.'}
                                </li>
                                <li><strong>Name Matching:</strong> Reviews are analyzed for technician names mentioned in the text</li>
                                <li><strong>Smart Assignment:</strong> Reviews are automatically assigned to the matching technician</li>
                                <li><strong>Conflict Resolution:</strong> If multiple technicians have the same name, the review remains unassigned</li>
                                <li><strong>Manual Override:</strong> You can still manually assign or reassign reviews at any time</li>
                            </ul>
                        </motion.div>
                    </div>
                )}
                
                {activeTab === 'payments' && (
                    <PaymentManagement location={urlParams.location} user={urlParams.user} />
                )}
            </div>
        </div>
    );
};

export default AdminPortal;
